import React, {useEffect, useRef, useState} from 'react';
import axios from "axios";
import {MapPinIcon} from "@heroicons/react/20/solid";
import PlaceAutocompleteOption from "./place-autocomplete-option";
import {classNames, getLookup} from "../../../util/util-helpers";

export default function FieldPlaceAutocomplete({
                                              setLocations,
                                              stateFields,
                                              countryKey = 'CountryID',
                                              placeholderText = "Search for a place",
                                              autoFocus,
                                              isGlobalSearch,
                                              searchForAll,
                                              innerRef
                                          }) {
    const inputRef = useRef();
    const optionsListRef = useRef();
    const ignoreBlurRef = useRef(false);
    const isDropdownOnFocusDisabledRef = useRef(false);

    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [isDropdownVisible, setIsDropdownVisible] = useState(false);
    const [selectedOptionIndex, setSelectedOptionIndex] = useState(null);

    async function getPlaceDetails(placeId) {
        const response = await axios.get(
            `https://places.googleapis.com/v1/places/${placeId}`,
            {
                headers: {
                    "X-Goog-Api-Key": import.meta.env.VITE_GOOGLE_API_KEY,
                    "X-Goog-FieldMask":
                        "location,formattedAddress,displayName,addressComponents"
                }
            }
        );

        return response.data;
    }

    const getComponent = (components, type) => components?.find(c => c.types.includes(type));

    const parseAddress = (details) => {
        const components = details.addressComponents;

        const country = getComponent(components, "country");
        const city =
            getComponent(components, "locality") ||
            getComponent(components, "administrative_area_level_2");

        const state = getComponent(
            components,
            "administrative_area_level_1"
        );

        const street = getComponent(components, "route");
        const street_number = getComponent(details.addressComponents, "street_number");

        const postalCode = getComponent(components, "postal_code");

        return {
            country: country?.longText ?? "",              // United States
            countryCode: country?.shortText ?? "",         // US
            city: city?.longText ?? "",                    // New York
            state: state?.shortText ?? "",                 // NY
            stateFull: state?.longText ?? "",              // New York
            areaCode: postalCode?.longText ?? "",          // 10001,
            street: street?.longText ?? "",
            street_number: street_number?.longText ?? ""
        };
    };

    const handleSelectPlaceClick = async (place, ignoreFocus = false) => {
        try {
            const details = await getPlaceDetails(place.placeId);

            const address = parseAddress(details);

            const locationData = {
                LocationName: details.displayName?.text,
                LegalName: details.displayName?.text,
                AddressName: [address.street_number, address.street].filter(it => !!it).join(" "),
                AddressName2: "",
                CityName: address.city,
                PostalCode: address.areaCode,
                query: place.text.text,
                StateID: getLookup('State', 'StateAbbreviation', 'StateID')?.[address?.state] ?? "",
                Latitude: details.location.latitude,
                Longitude: details.location.longitude
            }

            setQuery(place.text.text);
            setResults([]);
            setIsDropdownVisible(false);
            setSelectedOptionIndex(null);
            if (setLocations) {
                setLocations(locationData);
            }

            if (inputRef.current && !ignoreFocus) {
                isDropdownOnFocusDisabledRef.current = true;
                inputRef.current.focus();
            }
        } catch (err) {
            console.error("Failed to fetch place details", err);
        }
    };

    const handleInputChange = (e) => {
        setQuery(e.target.value);
        setIsDropdownVisible(true)
    }

    const handleMouseDown = (e) => {
        if (optionsListRef.current && optionsListRef.current.contains(e.target)) {
            ignoreBlurRef.current = true;
        }
    };

    const handleBlur = () => {
        if (ignoreBlurRef.current) {
            ignoreBlurRef.current = false;
            return;
        }

        setIsDropdownVisible(false);
        if (selectedOptionIndex !== null && results[selectedOptionIndex]) {
            void handleSelectPlaceClick(results[selectedOptionIndex]);
        }
    }

    const handleKeyDown = (e) => {
        if (e.key === "ArrowDown") {
            setSelectedOptionIndex(prev => prev + 1 > results.length - 1 ? 0 : prev + 1);
        }

        if (e.key === "ArrowUp") {
            setSelectedOptionIndex(prev => prev - 1 < 0 ? results.length - 1 : prev - 1);
        }

        if (e.key === "Enter" && selectedOptionIndex !== null && results[selectedOptionIndex]) {
            void handleSelectPlaceClick(results[selectedOptionIndex]);
        }

        if (e.key === "tab") {
            ignoreBlurRef.current = true;

            if (selectedOptionIndex !== null && results[selectedOptionIndex]) {
                void handleSelectPlaceClick(results[selectedOptionIndex], true);
            }
        }
    }

    useEffect(() => {
        if (query.length < 3) {
            setResults([]);
            return;
        }

        const controller = new AbortController();
        let countryCode = "US"
        if (stateFields?.[countryKey]?.value) {
            countryCode = getLookup("Country", "CountryID", "Abbreviation")?.[stateFields?.[countryKey]?.value];
        }

        const body = {
            input: query,
        };


        if (countryCode && (!isGlobalSearch && !searchForAll)) {
            body.includedRegionCodes = [countryCode];
        }

        fetch("https://places.googleapis.com/v1/places:autocomplete", {
            method: "POST",
            signal: controller.signal,
            headers: {
                "Content-Type": "application/json",
                "X-Goog-Api-Key": import.meta.env.VITE_GOOGLE_API_KEY,
                "X-Goog-FieldMask":
                    "suggestions.placePrediction.placeId,suggestions.placePrediction.text"
            },
            body: JSON.stringify(body)
        })
            .then(res => res.json())
            .then(data => {
                const predictions =
                    data?.suggestions?.map(s => s.placePrediction) ?? [];
                setResults(predictions);
            })
            .catch(() => {
            });

        return () => controller.abort();
    }, [query]);

    return (
        <div className="relative w-full">
            <input
                autoFocus={autoFocus}
                ref={innerRef ?? inputRef}
                className="form-control"
                value={query}
                onChange={handleInputChange}
                onBlur={handleBlur}
                onFocus={(e) => {
                    if (!isDropdownOnFocusDisabledRef.current) {
                        setIsDropdownVisible(true);
                        e.target.select();
                    }

                    isDropdownOnFocusDisabledRef.current = false;
                }}
                onMouseDown={handleMouseDown}
                onKeyDown={handleKeyDown}
                placeholder={placeholderText}
            />

            {results.length > 0 && isDropdownVisible && (
                <ul
                    ref={optionsListRef}
                    onMouseDown={handleMouseDown}
                    className="absolute z-50 mt-1 w-full max-w-md overflow-auto rounded-card border border-tm-gray-200 bg-popup shadow-lg divide-y divide-y-tm-gray-300"
                >
                    {results.map((place, index) => {
                        const isSelected = selectedOptionIndex === index;
                        return <li
                            key={place?.placeId}
                            onClick={() => {
                                void handleSelectPlaceClick(place);
                            }}
                            className={
                                classNames(
                                    "flex items-center cursor-pointer px-2 py-1.5 text-xs 0 hover:bg-primary/5",
                                    isSelected ? "text-tm-gray-700 bg-primary/5" : "text-tm-gray-700"
                                )
                            }
                        >
                            <MapPinIcon
                                className={classNames(
                                    "size-4 mr-2 shrink-0",
                                    isSelected ? "text-primary" : "text-tm-gray-500"
                                )}
                            />

                            <PlaceAutocompleteOption
                                query={query}
                                place={place}
                            />
                        </li>
                    })}
                </ul>
            )}
        </div>
    );
}
